import api from 'api';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { useEffect, useState, useRef } from 'react';
import { LTHeader, LTItem, LTRow, LTPlaceholder, LTMobileItem } from 'components/common/ListTable';
import Skeleton from '@mui/material/Skeleton';
import Checkbox from 'components/common/Checkbox/Checkbox';
import CheckboxWithLabel from 'components/common/CheckboxWithLabel/CheckboxWithLabel';
import { setActiveApp } from 'redux/application/application.slice';
import { useAuth } from 'contexts/ProtectedRoute';
import { useTranslation } from 'react-i18next';
import { CustomLTRow } from '../customLTRow/customLTRow';
import * as S from './InvoicesTable.styled';
import ROLE from '../../../../../assets/userRoles.json';
import { InvoiceResponse } from 'api/models/response/Invoices';
import IconBtn from 'components/common/IconBtn';
import { ReactComponent as DownloadIcon } from 'assets/icons/downloadB2b.svg';
import { ReactComponent as VisibilityOffIcon } from 'assets/icons/visibility_off.svg';
import { ReactComponent as SendArrowIcon } from 'assets/icons/arrowInWhite.svg';
import ToggleableDropdown from 'components/common/ToggleableDropdown/ToggleableDropdown';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { programNamesForCountries } from 'utils/transformProgramsToResults';
import { AgentRoles } from 'api/models/payload/AgentRoles';
import StatusDropdown from 'components/common/StatusDropdown/StatusDropdown';
import PrimaryModal from 'components/common/PrimaryModal/PrimaryModal';
import PDFViewer from 'components/common/PDFViewer/PDFViewer';
import { accTypeOptions, statusOptions } from 'data/constants/paymentStatuse';

export type InvoiceFilter = {
    candidate_id?: string | null;
    milestone_id?: string | null;
    payment_status?: string | null;
    program_name?: string | null;
    consultant_id?: string | null;
    account_type?: string | null;
    sp_id?: string | null;
};

type InvoicesTableProps = {
    invoices: InvoiceResponse[];
    invoicesForTable: InvoiceResponse[];
    setInvoicesForTable: (apps: InvoiceResponse[]) => void;
    isClientsAppsLoading: boolean;
    onFilterChange: (filters: InvoiceFilter) => void;
    mobile: boolean;
    invoiceListRefetch: () => void;
};

type OptionType = {
    value: string;
    label: string;
};
type StatusOption = {
    label: string;
    color: string;
    value?: string;
};

const statusDropdownOptions = [
    { label: 'Refunded', value: 'REFUNDED' },
    { label: 'Fully Paid', value: 'FULLY_PAID' },
    { label: 'Partially Paid', value: 'PARTIALLY_PAID' },
    { label: 'Payment Pending', value: 'PENDING' },
];

const accTypeDropdownOptions = [
    { label: 'Business Account', value: 'BUSINESS' },
    { label: 'Trust Account', value: 'TRUST' },
];

const InvoicesTable = ({
    invoices,
    invoicesForTable,
    setInvoicesForTable,
    isClientsAppsLoading,
    onFilterChange,
    mobile,
    invoiceListRefetch,
}: InvoicesTableProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { names: enabledCountryNames } = useEnabledCountries();
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const tableRef = useRef<HTMLDivElement | null>(null);
    const [selectedApps, setSelectedApps] = useState<number[]>([]);
    const [checkedHeader, setCheckedHeader] = useState<boolean>(false);
    const [countryBasedProgramList, setCountryBasedProgramList] = useState<OptionType[]>([]);
    const [matchingClients, setMatchingClients] = useState<OptionType[]>([]);
    const [visaAgents, setVisaAgents] = useState<OptionType[]>([]);
    const [selectedCaseProgram, setSelectedCaseProgram] = useState<string>('');
    const [selectedClient, setSelectedClient] = useState<string>('');
    const [selectedConsultant, setSelectedConsultant] = useState<string>('');
    const [selectedAccType, setSelectedAccType] = useState<string>('');
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string>('');

    useEffect(() => {
        onFilterChange({
            candidate_id: selectedClient || null,
            program_name: selectedCaseProgram || null,
            consultant_id: selectedConsultant || null,
            payment_status: selectedPaymentStatus || null,
            account_type: selectedAccType || null,
        });
    }, [
        selectedClient,
        selectedPaymentStatus,
        selectedCaseProgram,
        selectedConsultant,
        selectedAccType,
        onFilterChange,
    ]);

    const switchAccountType = async (appId: string, milestoneId: string, accType: string) => {
        try {
            const resp = await api.application.editAccType(appId, milestoneId, accType);
            if (resp.status === 200 || resp.status === 201) {
                invoiceListRefetch();
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        fetchProgramList();
        fetchMatchingClients();
        fetchAgents();
        // Program names follow the enabled country set.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabledCountryNames]);

    const fetchProgramList = async () => {
        try {
            const response = await api.expertiseList.get();
            const programArray = programNamesForCountries(response.data, enabledCountryNames);
            setCountryBasedProgramList(programArray.map((e) => ({ value: e, label: e })));
        } catch (e) {
            console.error(e);
        }
    };

    const fetchMatchingClients = async (input?: string) => {
        try {
            const resp = await api.clientList.getClientList(undefined, undefined, input);
            if (resp.status === 200 || resp.status === 201) {
                setMatchingClients(
                    resp.data.map((e) => ({
                        value: e.id.toString(),
                        label: e.firstname + ' ' + e.lastname,
                    }))
                );
            }
        } catch (error) {
            console.error(error);
        }
    };
    const fetchAgents = async () => {
        try {
            const resp = await api.agents.get(AgentRoles.CONSULTANT);
            if (resp.status === 200 || resp.status === 201) {
                setVisaAgents(
                    resp.data.map((e) => ({
                        value: e.id.toString(),
                        label: e.firstname + ' ' + e.lastname,
                    }))
                );
            }
        } catch (e) {
            console.error(e);
        }
    };

    const editMilestonePaymentStatus = async (
        appId: string,
        milestoneId: string,
        payload: string
    ) => {
        try {
            await api.application.editMilestonesPaymentStatus(appId, milestoneId, payload);
        } catch (err) {
            console.log(err);
        }
    };

    const skeletonArray = invoicesForTable.length > 0 ? invoicesForTable : [1, 2, 3];
    const { role } = useAuth();
    const canAssignClients =
        role !== ROLE.sales && role !== ROLE.consultant && role !== ROLE.backofficeConsultant;

    const invoiceGridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: canAssignClients
            ? '24px 80px 200px 200px 200px minmax(90px, 1fr) 190px 90px 100px 150px'
            : '80px 200px 200px 200px minmax(90px, 1fr) 190px 90px 100px 150px',
        alignItems: 'center',
        columnGap: '0px',
        width: '100%',
    };

    const invoiceCellBaseStyle: React.CSSProperties = {
        width: '100%',
        minWidth: 0,
    };

    const invoiceHeaderCellStyle: React.CSSProperties = {
        ...invoiceCellBaseStyle,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        textAlign: 'center',
        whiteSpace: 'nowrap',
    };

    const invoiceRowCellStyle: React.CSSProperties = {
        ...invoiceCellBaseStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
    };

    useEffect(() => {
        dispatch(setActiveApp(null));
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        localStorage.setItem('selectedApps', JSON.stringify(selectedApps));
    }, [selectedApps]);

    useEffect(() => {
        //this is needed to reset the select app checkboxes after assigning clients
        setSelectedApps([]);
        setCheckedHeader(false);
    }, [invoices]);

    useEffect(() => {
        setInvoicesForTable(invoices);
    }, [invoices, setInvoicesForTable]);

    const handleSelectApp = async (appID: number, checked: boolean) => {
        if (checked) {
            setSelectedApps((prevApps) => [...prevApps, appID]);
        } else {
            setSelectedApps((prevApps) => prevApps.filter((id) => id !== appID));
        }
    };

    const handleChangeHeader = (checkedStatus: boolean) => {
        setCheckedHeader(checkedStatus);

        if (checkedStatus) {
            const listOfAllCases = invoicesForTable.map((clientCase) => clientCase.id);
            setSelectedApps(listOfAllCases);
        } else {
            setSelectedApps([]);
        }
    };

    function formatCurrency(amount: number | null) {
        if (amount) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        } else {
            return '$0';
        }
    }

    const selectionTitle = (
        <>
            {canAssignClients && (
                <LTItem
                    tip={t('selectAll', { lng: language })}
                    style={{
                        ...invoiceHeaderCellStyle,
                        whiteSpace: 'normal',
                        lineHeight: '1.2',
                    }}
                >
                    <Checkbox
                        onChange={(ev) => {
                            handleChangeHeader((ev.target as HTMLInputElement).checked);
                        }}
                        checked={checkedHeader}
                    />
                </LTItem>
            )}
        </>
    );
    const downloadInvoice = async (appId: string, milestoneId: string) => {
        try {
            const resp = await api.application.getMilestoneInvoice(appId, milestoneId);
            const blob = new Blob([resp.data], { type: 'application/pdf' });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `invoice-${milestoneId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.log(err);
        }
    };
    const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);

    const viewInvoice = async (appId: string, milestoneId: string) => {
        try {
            const resp = await api.application.getMilestoneInvoice(appId, milestoneId);
            const blob = new Blob([resp.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            setInvoiceUrl(url);
        } catch (err) {
            console.error(err);
        }
    };

    const sendInvoice = async (appId: string, milestoneId: string) => {
        try {
            await api.application.sendInvoiceEmail(appId, milestoneId);
        } catch (e) {
            console.error(e);
        }
    };

    const [openInvoiceDialod, setOpenInvoiceDialog] = useState<boolean>(false);
    const openCloseInvoiceDialog = async (appId?: string, milestoneId?: string) => {
        setOpenInvoiceDialog((prevState) => !prevState);
        if (appId && milestoneId) {
            viewInvoice(appId, milestoneId);
        }
    };

    return (
        <>
            <S.ListTable
                ref={tableRef}
                style={
                    mobile
                        ? { maxHeight: 'unset', width: '100%', maxWidth: '1360px' }
                        : { marginTop: '30px', overflow: 'auto' }
                    // { maxHeight: 'unset', maxWidth: '1440px', zIndex: 1 }
                }
            >
                <div className="sticky top-0 bg-white z-10 pt-2">
                    <LTHeader innerStyle={mobile ? undefined : invoiceGridStyle}>
                        {/* tooltips are present in LTItem itself */}
                        {selectionTitle}
                        <LTItem
                            tip={t('invoiceID', { lng: language })}
                            style={{
                                ...invoiceHeaderCellStyle,
                                whiteSpace: 'normal',
                                lineHeight: '1.2',
                            }}
                        >
                            {t('invoiceId', { lng: language })}
                        </LTItem>

                        <LTItem style={invoiceHeaderCellStyle}>
                            {t('status', { lng: language })}

                            <ToggleableDropdown
                                selectedType={selectedPaymentStatus}
                                handleSelectedType={(value) => setSelectedPaymentStatus(value)}
                                dropdownMenuPrograms={[
                                    { label: t('all', { lng: language }), value: '' },
                                    ...statusDropdownOptions,
                                ]}
                                purple={true}
                                label="selectCase"
                            />
                        </LTItem>
                        <LTItem style={invoiceHeaderCellStyle}>
                            {t('visaAgent', { lng: language })}
                            <ToggleableDropdown
                                selectedType={selectedConsultant}
                                handleSelectedType={(value) => setSelectedConsultant(value)}
                                dropdownMenuPrograms={[
                                    { label: t('all', { lng: language }), value: '' },
                                    ...visaAgents,
                                ]}
                                purple={true}
                                label="selectConsultant"
                            />
                        </LTItem>
                        <LTItem style={invoiceHeaderCellStyle}>
                            {t('clientName', { lng: language })}
                            <ToggleableDropdown
                                selectedType={selectedClient}
                                handleSelectedType={(value) => setSelectedClient(value)}
                                dropdownMenuPrograms={[
                                    { label: t('all', { lng: language }), value: '' },
                                    ...matchingClients,
                                ]}
                                purple={true}
                                label="selectCase"
                            />
                        </LTItem>
                        <LTItem style={invoiceHeaderCellStyle}>
                            {t('service', { lng: language })}
                            <ToggleableDropdown
                                selectedType={selectedCaseProgram}
                                handleSelectedType={(value) => setSelectedCaseProgram(value)}
                                dropdownMenuPrograms={[
                                    { label: t('all', { lng: language }), value: '' },
                                    ...countryBasedProgramList,
                                ]}
                                purple={true}
                                label="selectCase"
                            />
                        </LTItem>
                        <LTItem style={invoiceHeaderCellStyle}>
                            {t('Account Type', { lng: language })}
                            <ToggleableDropdown
                                selectedType={selectedAccType}
                                handleSelectedType={(value) => setSelectedAccType(value)}
                                dropdownMenuPrograms={[
                                    { label: t('all', { lng: language }), value: '' },
                                    ...accTypeDropdownOptions,
                                ]}
                                purple={true}
                                label="accountType"
                            />
                        </LTItem>
                        <LTItem tip="milestone" style={invoiceHeaderCellStyle}>
                            {t('#milestone', { lng: language })}
                        </LTItem>
                        <LTItem tip="amount" style={invoiceHeaderCellStyle}>
                            {t('amount', { lng: language })}
                        </LTItem>
                        <LTItem tip="actions" style={invoiceHeaderCellStyle}>
                            {t('actions', { lng: language })}
                        </LTItem>
                    </LTHeader>
                </div>

                {!isClientsAppsLoading && invoicesForTable.length === 0 && (
                    <LTPlaceholder>{t('theListIsEmpty', { lng: language })}</LTPlaceholder>
                )}

                {!isClientsAppsLoading && invoicesForTable.length > 0 && (
                    <>
                        <S.SpanForMobile>
                            <CustomLTRow>
                                {canAssignClients && (
                                    <CheckboxWithLabel
                                        label={t('selectAll', { lng: language })}
                                        gap="0px"
                                        bold
                                        labelStyles={{
                                            width: 'fit-content',
                                            whiteSpace: 'nowrap',
                                        }}
                                        sx={{
                                            display: 'flex',
                                            gap: '0.5rem',
                                            justifyContent: 'flex-start',
                                        }}
                                        onChange={(ev) => {
                                            handleChangeHeader(
                                                (ev.target as HTMLInputElement).checked
                                            );
                                        }}
                                        checked={checkedHeader}
                                        disabled={invoicesForTable?.length === 0}
                                    />
                                )}
                            </CustomLTRow>
                        </S.SpanForMobile>

                        {invoicesForTable.map((clientApp) => (
                            <CustomLTRow
                                key={`client_${clientApp.id}`}
                                id={`client_${clientApp.id}`}
                                wrapperStyle={mobile ? undefined : invoiceGridStyle}
                            >
                                {canAssignClients && (
                                    <LTItem
                                        tip={t('select', { lng: language })}
                                        style={invoiceRowCellStyle}
                                    >
                                        <Checkbox
                                            checked={selectedApps.includes(clientApp.id)}
                                            onChange={(__, checked) =>
                                                handleSelectApp(clientApp.id, checked)
                                            }
                                        />
                                    </LTItem>
                                )}

                                <LTItem style={invoiceRowCellStyle}>{clientApp.id}</LTItem>

                                <LTItem style={invoiceRowCellStyle}>
                                    <StatusDropdown
                                        options={statusOptions}
                                        onChange={(selected: StatusOption) => {
                                            const nextValue = selected.value ?? '';
                                            if (!nextValue) return;
                                            editMilestonePaymentStatus(
                                                clientApp.application_id.toString(),
                                                clientApp?.id.toString(),
                                                nextValue
                                            );
                                        }}
                                        initialValue={
                                            clientApp.payment_status
                                                ? clientApp.payment_status
                                                : 'PENDING'
                                        }
                                    />
                                </LTItem>
                                <LTItem style={invoiceRowCellStyle}>
                                    {clientApp.consultant_name ? clientApp.consultant_name : '--'}
                                </LTItem>
                                <LTItem style={invoiceRowCellStyle}>
                                    {clientApp.candidate_name ? clientApp.candidate_name : '--'}
                                </LTItem>
                                <LTItem
                                    style={{
                                        ...invoiceRowCellStyle,
                                        justifyContent: 'flex-start',
                                        textAlign: 'left',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {clientApp.program_name ? clientApp.program_name : '--'}
                                </LTItem>
                                <LTItem style={invoiceRowCellStyle}>
                                    {/* aka */}
                                    <StatusDropdown
                                        options={accTypeOptions}
                                        onChange={(selected: StatusOption) => {
                                            const nextValue = selected.value ?? '';
                                            if (!nextValue) return;
                                            switchAccountType(
                                                clientApp.application_id.toString(),
                                                clientApp?.id.toString(),
                                                nextValue
                                            );
                                        }}
                                        initialValue={
                                            clientApp.account_type ? clientApp.account_type : '--'
                                        }
                                    />
                                </LTItem>
                                <LTItem style={invoiceRowCellStyle}>
                                    {clientApp.milestone_index ? clientApp.milestone_index : '--'}
                                </LTItem>
                                <LTItem style={invoiceRowCellStyle}>
                                    {formatCurrency(clientApp.price)}
                                </LTItem>
                                <LTItem style={invoiceRowCellStyle}>
                                    <IconBtn
                                        variant="primary"
                                        onClick={() => {
                                            openCloseInvoiceDialog(
                                                clientApp.application_id.toString(),
                                                clientApp?.id.toString()
                                            );
                                        }}
                                    >
                                        <VisibilityOffIcon fill={'#282828'} />
                                    </IconBtn>
                                    <IconBtn
                                        variant="primary"
                                        onClick={() => {
                                            downloadInvoice(
                                                clientApp.application_id.toString(),
                                                clientApp?.id.toString()
                                            );
                                        }}
                                    >
                                        <DownloadIcon fill={'#282828'} />
                                    </IconBtn>

                                    <IconBtn
                                        variant="primary"
                                        onClick={() => {
                                            if (
                                                clientApp?.id !== undefined &&
                                                clientApp?.id !== undefined
                                            ) {
                                                sendInvoice(
                                                    clientApp.application_id.toString(),
                                                    clientApp.id.toString()
                                                );
                                            }
                                        }}
                                    >
                                        <SendArrowIcon />
                                    </IconBtn>
                                </LTItem>

                                {/*SMALL SCREEN VERSION*/}
                                {canAssignClients && (
                                    <LTMobileItem
                                        style={{ minWidth: '132px' }}
                                        name={t('assign', { lng: language })}
                                    >
                                        <Checkbox
                                            checked={selectedApps.includes(clientApp.id)}
                                            onChange={(__, checked) =>
                                                handleSelectApp(clientApp.id, checked)
                                            }
                                        />
                                    </LTMobileItem>
                                )}

                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('invoiceID', { lng: language })}
                                >
                                    {clientApp.id}
                                </LTMobileItem>

                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('status', { lng: language })}
                                >
                                    <StatusDropdown
                                        options={statusOptions}
                                        onChange={(selected: StatusOption) => {
                                            const nextValue = selected.value ?? '';
                                            if (!nextValue) return;
                                            editMilestonePaymentStatus(
                                                clientApp.application_id.toString(),
                                                clientApp?.id.toString(),
                                                nextValue
                                            );
                                        }}
                                        initialValue={
                                            clientApp.payment_status
                                                ? clientApp.payment_status
                                                : 'PENDING'
                                        }
                                    />
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('visaAgent', { lng: language })}
                                >
                                    {clientApp.consultant_name ? clientApp.consultant_name : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('clientName', { lng: language })}
                                >
                                    {clientApp.candidate_name ? clientApp.candidate_name : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('service', { lng: language })}
                                >
                                    {clientApp.program_name ? clientApp.program_name : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('accountType', { lng: language })}
                                >
                                    {clientApp.account_type ? clientApp.account_type : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('#milestone', { lng: language })}
                                >
                                    {clientApp.milestone_index ? clientApp.milestone_index : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('amount', { lng: language })}
                                >
                                    {clientApp.price ? clientApp.price : '--'}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('actions', { lng: language })}
                                >
                                    {clientApp.price ? clientApp.price : '--'}
                                </LTMobileItem>
                            </CustomLTRow>
                        ))}
                    </>
                )}

                {isClientsAppsLoading &&
                    skeletonArray.map((_, idx) => (
                        <LTRow key={idx}>
                            <LTItem maxWidth="12%">
                                <Skeleton width={40} />
                            </LTItem>
                            <LTItem maxWidth="12%">
                                <Skeleton width={40} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="35%">
                                <Skeleton width={200} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="14%">
                                <Skeleton width={79} />
                            </LTItem>
                            <LTItem minWidth="14%">
                                <Skeleton width={79} />
                            </LTItem>
                            <LTItem minWidth="14%">
                                <Skeleton width={79} />
                            </LTItem>
                            <LTItem minWidth="12%" lastItem>
                                <Skeleton width={79} />
                            </LTItem>
                        </LTRow>
                    ))}
                {invoiceUrl && (
                    <PrimaryModal
                        open={openInvoiceDialod}
                        onClose={openCloseInvoiceDialog}
                        maxWidth="640px"
                    >
                        <div
                            style={{
                                marginTop: '1rem',
                                border: '2px solid #8353E2',
                                borderRadius: '6px',
                                overflow: 'hidden',
                            }}
                        >
                            <PDFViewer file={invoiceUrl} />
                        </div>
                    </PrimaryModal>
                )}
            </S.ListTable>
        </>
    );
};

export default InvoicesTable;
